import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ContactTab = ({ practitionerData, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    referralEmail: '',
    phone: '',
    address: '',
    city: '',
    postalCode: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (practitionerData) {
      setFormData({
        email: practitionerData?.email || '',
        referralEmail: practitionerData?.referralEmail || '',
        phone: practitionerData?.phone || '',
        address: practitionerData?.address || '',
        city: practitionerData?.city || '',
        postalCode: practitionerData?.postalCode || ''
      });
    }
  }, [practitionerData]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
    // Clear error for this field
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData?.email && !emailRegex?.test(formData?.email)) {
      newErrors.email = 'Email invalide';
    }
    if (formData?.referralEmail && !emailRegex?.test(formData?.referralEmail)) {
      newErrors.referralEmail = 'Email invalide';
    }

    // Postal code validation (French format)
    const postalCodeRegex = /^\d{5}$/;
    if (formData?.postalCode && !postalCodeRegex?.test(formData?.postalCode)) {
      newErrors.postalCode = 'Code postal invalide (5 chiffres)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Email Configuration */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} className="text-accent" />
          Configuration des emails
        </h3>
        <div className="space-y-4">
          <div className="bg-accent/10 border border-accent/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">Email de notification</p>
                <p className="text-sm text-secondary mt-1">
                  L'email de notification recevra une copie de chaque nouveau formulaire d'adressage soumis.
                </p>
              </div>
            </div>
          </div>
          <Input
            label="Email principal"
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleInputChange}
            placeholder="contact@cabinet.fr"
            error={errors?.email}
            required
          />
          <Input
            label="Email de notification des adressages"
            name="referralEmail"
            type="email"
            value={formData?.referralEmail}
            onChange={handleInputChange}
            placeholder="adressages@cabinet.fr"
            error={errors?.referralEmail}
            description="Si différent de l'email principal"
          />
        </div>
      </div>
      {/* Phone */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Phone" size={20} className="text-accent" />
          Téléphone
        </h3>
        <Input
          label="Numéro de téléphone"
          name="phone"
          type="tel"
          value={formData?.phone}
          onChange={handleInputChange}
          placeholder="03 81 XX XX XX"
        />
      </div>
      {/* Address */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="MapPin" size={20} className="text-accent" />
          Adresse du cabinet
        </h3>
        <div className="space-y-4">
          <Input
            label="Adresse"
            name="address"
            value={formData?.address}
            onChange={handleInputChange}
            placeholder="15 rue Lavoisier"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Ville"
              name="city"
              value={formData?.city}
              onChange={handleInputChange}
              placeholder="Besançon"
            />
            <Input
              label="Code postal"
              name="postalCode"
              value={formData?.postalCode}
              onChange={handleInputChange}
              placeholder="25000"
              error={errors?.postalCode}
            />
          </div>
        </div>
      </div>
      {/* Contact Summary */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-accent" />
          Récapitulatif
        </h3>
        <div className="bg-card-elevated rounded-lg p-6 space-y-3 border border-white/8">
          <div className="flex items-start gap-3">
            <Icon name="Mail" size={18} className="text-accent mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary">Email</p>
              <p className="text-sm text-secondary">{formData?.email || 'Non renseigné'}</p>
            </div>
          </div>
          {formData?.referralEmail && (
            <div className="flex items-start gap-3">
              <Icon name="Bell" size={18} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">Email de notification</p>
                <p className="text-sm text-secondary">{formData?.referralEmail}</p>
              </div>
            </div>
          )}
          {formData?.phone && (
            <div className="flex items-start gap-3">
              <Icon name="Phone" size={18} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">Téléphone</p>
                <p className="text-sm text-secondary">{formData?.phone}</p>
              </div>
            </div>
          )}
          {(formData?.address || formData?.city || formData?.postalCode) && (
            <div className="flex items-start gap-3">
              <Icon name="MapPin" size={18} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">Adresse</p>
                <p className="text-sm text-secondary">
                  {formData?.address && `${formData?.address}, `}
                  {formData?.postalCode} {formData?.city}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t border-white/8">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
          disabled={!hasChanges || saving}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          loading={saving}
          disabled={!hasChanges || saving}
          iconName="Save"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>
    </form>
  );
};

export default ContactTab;